import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, FileSpreadsheet, Info, Inbox, RotateCcw, Search, UploadCloud, X,
} from 'lucide-react';
import {
  downloadSpectrumFile,
  formatDriftEdit,
  formatDriftLineChange,
  formatDriftTotalsShift,
  formatDriftWhen,
  previewSpectrumCrew,
  pushSpectrumCrew,
  SpectrumDriftEmployee,
  SpectrumExportError,
  SpectrumPreview,
  SpectrumPreviewPhaseRow,
  SpectrumPreviewRow,
  SpectrumPushResult,
} from '../../api/wallcraftSpectrum';
import { PayPeriod } from '../../interfaces/PayPeriodInterface';
import { Supervisor } from '../../interfaces/SupervisorInterface';

/* ─────────────────────────────────────────────────────────────────── props */

export interface SpectrumExportModalProps {
  /** The board's Payroll Approved group — one supervisor's crew. */
  crew: Supervisor;
  payPeriodID: number;
  payPeriod: PayPeriod;
  onClose: () => void;
  /** Handed the push result so the page can raise its own success card. */
  onPushed: (result: SpectrumPushResult) => void;
}

type Phase = 'loading' | 'loadFailed' | 'select' | 'confirm' | 'pushing';

/* ────────────────────────────────────────────────────────────────── helpers */

// Duplicated from SageExportSelectModal for the same reason it duplicated them from the
// board: importing them back would be circular.
function initialsOfName(name: string): string {
  const parts = name.replace(',', ' ').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const [a, b] = [parts[0], parts[1] ?? ''];
  return `${a[0] ?? ''}${b[0] ?? ''}`.toUpperCase() || (a[0] ?? '?').toUpperCase();
}
function hashHue(s: string): number {
  let h = 7;
  for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) & 0x7fffffff;
  return h % 360;
}

/** Spectrum's own ordering, so the totals strip always reads R O D S. */
const PAY_TYPE_ORDER = ['R', 'O', 'D', 'S'];
const PAY_TYPE_LABEL: Record<string, string> = {
  R: 'Regular', O: 'Overtime', D: 'Double time', S: 'Sick',
};

function orderPayTypes(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const ia = PAY_TYPE_ORDER.indexOf(a);
    const ib = PAY_TYPE_ORDER.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a.localeCompare(b);
  });
}

/** "2026-09-14" → "Mon 9/14" without dragging the value through a local-time Date. */
function shortDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const dow = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
  return `${dow} ${Number(m[2])}/${Number(m[3])}`;
}

const money = (n: number) => `$${n.toFixed(2)}`;

/**
 * Wallcraft → Spectrum pre-time-card export for ONE supervisor's crew.
 *
 * Replaces the Pick-Up Sheet the payroll clerk used to key in by hand. Three server
 * calls, all scoped to (supervisorID, payPeriodID): preview (read-only), file (xlsx),
 * push (Spectrum's Data Exchange PreTimeCardBatch).
 *
 * The employee selection is applied CLIENT-SIDE to the previewed rows — the preview is
 * fetched once for the whole crew and the counts/totals recompute from it — so unchecking
 * somebody is instant and costs no round-trip. The selection is only sent to the server on
 * the file and push calls, where it actually decides what is written.
 */
const SpectrumExportModal: React.FC<SpectrumExportModalProps> = ({
  crew, payPeriodID, payPeriod, onClose, onPushed,
}) => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [preview, setPreview] = useState<SpectrumPreview | null>(null);
  const [loadError, setLoadError] = useState<SpectrumExportError | null>(null);
  const [actionError, setActionError] = useState<SpectrumExportError | null>(null);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [q, setQ] = useState('');
  const [fileBusy, setFileBusy] = useState(false);
  const [forcing, setForcing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const crewName = `${crew.name.last}, ${crew.name.first}`;
  const week = preview?.payPeriod.weekNumber ?? payPeriod.WeekNumber;

  /* ── load the crew's rows ──────────────────────────────────────────────── */

  useEffect(() => {
    let alive = true;
    setPhase('loading');
    setLoadError(null);
    (async () => {
      try {
        const data = await previewSpectrumCrew(crew.employeeID, payPeriodID);
        if (!alive) return;
        setPreview(data);
        // Opt-out: everyone starts checked except anyone already pushed to Spectrum.
        const pushed = new Set(data.alreadyPushedEmployeeIDs ?? []);
        const ids = Array.from(new Set(data.rows.map((r) => r.employeeID)));
        setSel(new Set(ids.filter((id) => !pushed.has(id))));
        setPhase('select');
      } catch (err) {
        if (!alive) return;
        setLoadError(err instanceof SpectrumExportError
          ? err
          : new SpectrumExportError(err instanceof Error ? err.message : 'Could not load this crew.'));
        setPhase('loadFailed');
      }
    })();
    return () => { alive = false; };
  }, [crew.employeeID, payPeriodID, reloadKey]);

  /* ── derived ───────────────────────────────────────────────────────────── */

  // Memoized because `?? []` would hand every dependent useMemo a brand-new array each render.
  const rows = useMemo(() => preview?.rows ?? [], [preview]);
  const pushedIDs = useMemo(
    () => new Set(preview?.alreadyPushedEmployeeIDs ?? []),
    [preview]);

  /** Distinct employees in the previewed rows, in the order the server sorted them. */
  const employees = useMemo(() => {
    const seen = new Map<number, { employeeID: number; altEmployeeID: string; name: string; rowCount: number; hours: number }>();
    rows.forEach((r) => {
      const hit = seen.get(r.employeeID);
      if (hit) {
        hit.rowCount += 1;
        if (r.lineKind === 'work' || r.lineKind === 'sick') hit.hours += r.hours ?? 0;
      } else {
        seen.set(r.employeeID, {
          employeeID: r.employeeID,
          altEmployeeID: r.altEmployeeID,
          name: r.name,
          rowCount: 1,
          hours: r.lineKind === 'work' || r.lineKind === 'sick' ? r.hours ?? 0 : 0,
        });
      }
    });
    return Array.from(seen.values());
  }, [rows]);

  const shown = useMemo(() => {
    const raw = q.trim().toLowerCase();
    if (!raw) return employees;
    const id = raw.startsWith('#') ? raw.slice(1) : raw;
    return employees.filter((e) =>
      e.name.toLowerCase().includes(raw)
      || (id !== '' && (e.altEmployeeID ?? '').toLowerCase().includes(id)));
  }, [employees, q]);

  const shownSelCount = shown.reduce((n, e) => n + (sel.has(e.employeeID) ? 1 : 0), 0);
  const allShownOn = shown.length > 0 && shownSelCount === shown.length;
  const someShown = shownSelCount > 0 && !allShownOn;
  const filtering = q.trim().length > 0;

  const selectedRows: SpectrumPreviewRow[] = useMemo(
    () => rows.filter((r) => sel.has(r.employeeID)),
    [rows, sel]);

  /**
   * The purchase-order phases behind the CHECKED rows — the push creates each of them on
   * the job (AddPhase) before it sends a single pre-time card. Unticking the only employee
   * on a PO drops it again. An API build that sends neither `isPoPhase` nor `phaseRows`
   * simply reads as "no PO phases", which is what the old push did.
   */
  const selectedPoKeys = useMemo(
    () => new Set(
      selectedRows
        .filter((r) => r.isPoPhase && r.jobNumber && r.phaseCode)
        .map((r) => `${r.jobNumber}|${r.phaseCode}`)),
    [selectedRows]);

  /** Two rows per PO — cost type 1 and 2 — in the order the server built them. */
  const selectedPhaseRows: SpectrumPreviewPhaseRow[] = useMemo(
    () => (preview?.phaseRows ?? [])
      .filter((p) => selectedPoKeys.has(`${p.jobNumber}|${p.phaseCode}`)),
    [preview, selectedPoKeys]);

  /** Counts recomputed from the selection — never the server's whole-crew figures. */
  const totals = useMemo(() => {
    const hours: Record<string, number> = {};
    let bonusCount = 0;
    let bonusAmount = 0;
    let mealCount = 0;
    let phoneCount = 0;
    let phoneAmount = 0;
    selectedRows.forEach((r) => {
      if (r.lineKind === 'bonus') { bonusCount += 1; bonusAmount += r.payRate; return; }
      if (r.lineKind === 'phone') { phoneCount += 1; phoneAmount += r.payRate; return; }
      if (r.lineKind === 'meal') { mealCount += 1; return; }
      hours[r.payType] = (hours[r.payType] ?? 0) + (r.hours ?? 0);
    });
    const total = Object.values(hours).reduce((s, h) => s + h, 0);
    return { hours, bonusCount, bonusAmount, mealCount, phoneCount, phoneAmount, total };
  }, [selectedRows]);

  const selectedIDs = useMemo(
    () => employees.filter((e) => sel.has(e.employeeID)).map((e) => e.employeeID),
    [employees, sel]);
  const selectedPushed = useMemo(
    () => selectedIDs.filter((id) => pushedIDs.has(id)),
    [selectedIDs, pushedIDs]);

  /** Pushed employees whose live timecard no longer matches — absent on older API builds. */
  const changedSincePush: SpectrumDriftEmployee[] = useMemo(
    () => preview?.changedSincePush ?? [],
    [preview]);

  const hasPreview = preview != null;
  const canPush = hasPreview && selectedIDs.length > 0 && selectedPushed.length === 0;
  const canForce = hasPreview && selectedIDs.length > 0 && selectedPushed.length > 0;

  /* ── actions ───────────────────────────────────────────────────────────── */

  const toggle = (id: number) => setSel((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAllShown = (on: boolean) => setSel((prev) => {
    const next = new Set(prev);
    shown.forEach((e) => { if (on) next.add(e.employeeID); else next.delete(e.employeeID); });
    return next;
  });

  const asExportError = (err: unknown): SpectrumExportError => (
    err instanceof SpectrumExportError
      ? err
      : new SpectrumExportError(err instanceof Error ? err.message : 'The Spectrum export failed.')
  );

  const download = async () => {
    if (!preview || fileBusy) return;
    setFileBusy(true);
    setActionError(null);
    try {
      await downloadSpectrumFile(
        crew.employeeID,
        payPeriodID,
        `SpectrumPreTime-${payPeriod.AccountingYear}-Week${week}-${preview.crewCode}.xlsx`,
        selectedIDs
      );
    } catch (err) {
      setActionError(asExportError(err));
    } finally {
      setFileBusy(false);
    }
  };

  const openConfirm = (force: boolean) => {
    setForcing(force);
    setActionError(null);
    setPhase('confirm');
  };

  const push = async () => {
    if (!preview) return;
    setPhase('pushing');
    setActionError(null);
    try {
      const result = await pushSpectrumCrew(crew.employeeID, payPeriodID, selectedIDs, forcing);
      onPushed(result);
    } catch (err) {
      setActionError(asExportError(err));
      setPhase('select');
    }
  };

  /* ── shared chrome ─────────────────────────────────────────────────────── */

  const metaLine = [
    crewName,
    preview ? `Crew ${preview.crewCode}` : null,
    preview ? `Batch ${preview.batchCode}` : null,
    `Week ${week}`,
  ].filter(Boolean).join(' · ');

  const closable = phase !== 'pushing';

  const head = (icon: React.ReactNode, tone: string, title: string, sub: string) => (
    <div className="modal-head">
      <span className={`mi${tone ? ` ${tone}` : ''}`}>{icon}</span>
      <div>
        <h3>{title}</h3>
        <div className="ms">{sub}</div>
      </div>
      {closable && <button className="x" onClick={onClose} type="button" aria-label="Close"><X /></button>}
    </div>
  );

  /** The 422 payload — our own validator or Spectrum's own refusal, same shape. */
  const rejectionList = (error: SpectrumExportError) => (
    <div className="vx-note bad sx-rej">
      <AlertTriangle />
      <span>
        <b>{error.message}</b>
        {!!error.rejections?.length && (
          <ul className="sx-rejgrp">
            {error.rejections.map((emp) => (
              <li key={`${emp.employeeID}-${emp.altEmployeeID}`}>
                <b>Emp {emp.altEmployeeID || emp.employeeID || '—'}</b>
                <ul>
                  {(emp.issues ?? []).map((r, i) => (
                    <li key={`${r.workDate}-${r.field}-${r.code}-${i}`}>
                      <span className="sx-rejline">
                        Emp {emp.altEmployeeID || emp.employeeID} {r.workDate} {r.field} — {r.code}: {r.value}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </span>
    </div>
  );

  const size = phase === 'pushing' ? '' : 'xl';

  return (
    <div className="trb-modal-bg" onClick={() => { if (closable) onClose(); }}>
      <div className={`trb-modal vx-modal${size ? ` ${size}` : ''}`} onClick={(e) => e.stopPropagation()}>

        {/* ── loading ───────────────────────────────────────────────────── */}
        {phase === 'loading' && (
          <>
            {head(<UploadCloud />, '', 'Spectrum Export', metaLine)}
            <div className="modal-body">
              <div className="vx-starting">
                <div className="vx-spin" />
                <span className="t">Loading this crew&rsquo;s pre-time card rows…</span>
                <span className="s">Nothing has been sent to Spectrum.</span>
              </div>
            </div>
          </>
        )}

        {/* ── preview refused (422 validation, 404, 503, …) ─────────────── */}
        {phase === 'loadFailed' && loadError && (
          <>
            {head(<AlertTriangle />, 'bad', 'Spectrum Export blocked', metaLine)}
            <div className="modal-body">
              {rejectionList(loadError)}
              <div className="vx-note info">
                <Info />
                <span>
                  Nothing was sent and nothing was saved. Fix the rows on the timesheets
                  (or the employee/job setup) and open this window again.
                </span>
              </div>
            </div>
            <div className="modal-foot tr-exp-foot">
              <span className="vx-grow" />
              <button className="tr-btn" onClick={() => setReloadKey((k) => k + 1)} type="button">
                <RotateCcw /> Retry
              </button>
              <button className="tr-btn" onClick={onClose} type="button">Close</button>
            </div>
          </>
        )}

        {/* ── selection ─────────────────────────────────────────────────── */}
        {phase === 'select' && preview && (
          <>
            {head(<UploadCloud />, '', 'Spectrum Export', metaLine)}
            <div className="modal-body tr-exp">
              {actionError && rejectionList(actionError)}

              {/* Timecards edited after their push: Spectrum still holds the old rows. */}
              {changedSincePush.length > 0 && (
                <div className="vx-note warn sx-drift">
                  <AlertTriangle />
                  <span>
                    <b>
                      {changedSincePush.length} employee{changedSincePush.length === 1 ? '' : 's'} changed
                      since the Spectrum push.
                    </b>
                    <ul className="sx-rejgrp">
                      {changedSincePush.map((d) => {
                        const edits = [...(d.edits ?? [])]
                          .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
                        const lines = d.lineChanges ?? [];
                        return (
                          <li key={d.employeeID}>
                            <b>{d.name} #{d.altEmployeeID}</b>
                            {' '}&middot; pushed {formatDriftWhen(d.pushedAt)} &middot; {formatDriftTotalsShift(d)}
                            {!d.stillApproved && <> &middot; <b>No longer Payroll Approved</b></>}
                            <ul>
                              {lines.slice(0, 6).map((c, i) => (
                                <li key={`l-${i}`}><span className="sx-rejline">{formatDriftLineChange(c)}</span></li>
                              ))}
                              {lines.length > 6 && <li key="l-more">…and {lines.length - 6} more line changes</li>}
                              {edits.slice(0, 3).map((e, i) => (
                                <li key={`e-${i}`}>{formatDriftEdit(e)}</li>
                              ))}
                              {edits.length > 3 && <li key="e-more">…and {edits.length - 3} more edits</li>}
                            </ul>
                          </li>
                        );
                      })}
                    </ul>
                    Spectrum has no delete: remove these employees&rsquo; earlier rows in Spectrum first,
                    then re-push them (Re-push selected (force)). A forced push APPENDS to the same
                    batch — if the earlier rows are still there the hours will be counted twice.
                  </span>
                </div>
              )}

              {employees.length === 0 ? (
                <div className="vx-deadend">
                  <span className="tile"><Inbox /></span>
                  <h4>Nothing to export for this crew</h4>
                  <p>
                    The preview came back with no pre-time card rows for {crewName} in Week {week}.
                  </p>
                </div>
              ) : (
                <>
                  <p className="tr-exp-intro">
                    Everyone is selected except anyone already pushed. Unchecking somebody
                    only changes this window — the totals and the row list below recompute
                    from what is checked, and only checked employees are written to the xlsx
                    or sent to Spectrum.
                  </p>

                  {/* Totals recomputed from the checked employees, not the whole crew. */}
                  <div className="sx-totals">
                    {orderPayTypes(Object.keys(totals.hours)).map((pt) => (
                      <div className="sx-tot" key={pt}>
                        <span className="lb">{PAY_TYPE_LABEL[pt] ?? pt} ({pt})</span>
                        <span className="vl">{totals.hours[pt].toFixed(2)}</span>
                        <span className="sb">hours</span>
                      </div>
                    ))}
                    <div className="sx-tot">
                      <span className="lb">Total</span>
                      <span className="vl">{totals.total.toFixed(2)}</span>
                      <span className="sb">hours</span>
                    </div>
                    <div className={`sx-tot${totals.bonusCount === 0 ? ' off' : ''}`}>
                      <span className="lb">Production bonus</span>
                      <span className="vl">{totals.bonusCount}</span>
                      <span className="sb">{totals.bonusCount ? money(totals.bonusAmount) : 'no rows'}</span>
                    </div>
                    <div className={`sx-tot${totals.phoneCount === 0 ? ' off' : ''}`}>
                      <span className="lb">Phone reimbursement</span>
                      <span className="vl">{totals.phoneCount}</span>
                      <span className="sb">{totals.phoneCount ? money(totals.phoneAmount) : 'no rows'}</span>
                    </div>
                    <div className={`sx-tot${totals.mealCount === 0 ? ' off' : ''}`}>
                      <span className="lb">Meal premium</span>
                      <span className="vl">{totals.mealCount}</span>
                      <span className="sb">{totals.mealCount === 1 ? 'row' : 'rows'}</span>
                    </div>
                    <div className={`sx-tot${selectedPoKeys.size === 0 ? ' off' : ''}`}>
                      <span className="lb">PO phases</span>
                      <span className="vl">{selectedPoKeys.size}</span>
                      <span className="sb">{selectedPoKeys.size ? 'created first' : 'none'}</span>
                    </div>
                    <div className="sx-tot">
                      <span className="lb">Rows</span>
                      <span className="vl">{selectedRows.length}</span>
                      <span className="sb">of {preview.rowCount}</span>
                    </div>
                  </div>

                  <div className="tr-exp-search">
                    <Search />
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Filter by employee name or ID…"
                      autoComplete="off"
                    />
                  </div>

                  <label className="tr-exp-all">
                    <input
                      type="checkbox"
                      ref={(el) => { if (el) el.indeterminate = someShown; }}
                      checked={allShownOn}
                      onChange={(e) => toggleAllShown(e.target.checked)}
                      disabled={shown.length === 0}
                    />
                    <span className="tr-exp-all-lab">{filtering ? 'Select all shown' : 'Select all'}</span>
                    <span className="tr-exp-count">
                      <b>{selectedIDs.length}</b> of <b>{employees.length}</b> selected
                      {filtering ? <> &middot; <b>{shown.length}</b> shown</> : null}
                    </span>
                  </label>

                  <div className="tr-exp-list">
                    {shown.length === 0 ? (
                      <div className="tr-noresult">
                        <Inbox />
                        <p>No employee matches &ldquo;<b>{q.trim()}</b>&rdquo;.</p>
                      </div>
                    ) : shown.map((e) => {
                      const hue = hashHue(e.name);
                      const on = sel.has(e.employeeID);
                      const already = pushedIDs.has(e.employeeID);
                      return (
                        <label key={e.employeeID} className={`tr-exp-row${on ? ' on' : ''}`}>
                          <input type="checkbox" checked={on} onChange={() => toggle(e.employeeID)} />
                          <span
                            className="tr-exp-av"
                            style={{ background: `hsl(${hue} 60% 92%)`, color: `hsl(${hue} 55% 38%)` }}
                          >
                            {initialsOfName(e.name)}
                          </span>
                          <span className="tr-exp-who">
                            <span className="tr-exp-nm">{e.name}</span>
                            <span className="tr-exp-sub">
                              #{e.altEmployeeID} &middot; {e.rowCount} row{e.rowCount === 1 ? '' : 's'} &middot; {e.hours.toFixed(2)} hrs
                            </span>
                          </span>
                          {already && (
                            <span className="vx-offtag" title={`Already pushed to Spectrum as batch ${preview.batchCode}.`}>
                              Pushed
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>

                  {selectedPushed.length > 0 && (
                    <div className="vx-note warn">
                      <AlertTriangle />
                      <span>
                        <b>
                          {selectedPushed.length} selected employee{selectedPushed.length === 1 ? ' was' : 's were'} already
                          pushed as batch {preview.batchCode}.
                        </b>{' '}
                        Spectrum has no delete: remove this crew&rsquo;s un-approved rows in Spectrum first,
                        then re-push. A normal push is blocked until they are unchecked.
                      </span>
                    </div>
                  )}

                  {/* The AddPhase calls that run before a single pre-time card is sent. */}
                  {selectedPhaseRows.length > 0 && (
                    <div className="sx-phases">
                      <div className="vx-note info">
                        <Info />
                        <span>
                          <b>PO phases created first.</b>{' '}
                          Added to the job in Spectrum before the pre-time cards are sent;
                          phases that already exist are skipped.
                        </span>
                      </div>
                      <div className="sx-rtbl">
                        <div className="sx-rth sx-phr">
                          <span>Co</span><span>Job</span><span>Phase</span>
                          <span>Description</span><span>Cost Type</span>
                        </div>
                        <div className="sx-rtb">
                          {selectedPhaseRows.map((p, i) => (
                            <div
                              className="sx-rtr sx-phr"
                              key={`${p.jobNumber}-${p.phaseCode}-${p.costType}-${i}`}
                            >
                              <span>{p.coCode || '—'}</span>
                              <span>{p.jobNumber || '—'}</span>
                              <span>{p.phaseCode || '—'}</span>
                              <span className="nm">{p.description || '—'}</span>
                              <span>{p.costType || '—'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* What actually goes over the wire — one line per Spectrum row. */}
                  <div className="sx-rtbl">
                    <div className="sx-rth">
                      <span>Employee</span><span>Date</span><span>Type</span><span>Hours</span>
                      <span>Job</span><span>Phase</span><span>Dept</span><span>Rate</span>
                    </div>
                    <div className="sx-rtb">
                      {selectedRows.length === 0 ? (
                        <div className="sx-rtempty">No employees selected.</div>
                      ) : selectedRows.map((r, i) => (
                        <div className={`sx-rtr k-${r.lineKind}`} key={`${r.employeeID}-${r.workDate}-${r.payType}-${r.lineKind}-${i}`}>
                          <span className="nm">{r.name}</span>
                          <span>{shortDate(r.workDate)}</span>
                          <span className="ty">{r.payType}</span>
                          <span className="num">{r.hours == null ? '' : r.hours.toFixed(2)}</span>
                          <span>{r.jobNumber || '—'}</span>
                          <span>{r.phaseCode || '—'}</span>
                          <span>{r.department || '—'}</span>
                          <span className="num">{r.payRate.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-foot tr-exp-foot">
              <button
                className="tr-btn"
                onClick={download}
                disabled={fileBusy || selectedIDs.length === 0}
                type="button"
                title="Download the same rows as a spreadsheet. Nothing is sent to Spectrum."
              >
                <FileSpreadsheet /> {fileBusy ? 'Generating…' : 'Download xlsx'}
              </button>
              <span className="vx-grow" />
              <button className="tr-btn" onClick={onClose} type="button">Cancel</button>
              {canForce ? (
                <button className="tr-btn danger" onClick={() => openConfirm(true)} type="button">
                  <UploadCloud /> Re-push selected (force)
                </button>
              ) : (
                <button
                  className="tr-btn primary"
                  onClick={() => openConfirm(false)}
                  disabled={!canPush}
                  type="button"
                >
                  <UploadCloud /> Push to Spectrum
                </button>
              )}
            </div>
          </>
        )}

        {/* ── confirm ───────────────────────────────────────────────────── */}
        {phase === 'confirm' && preview && (
          <>
            {head(<AlertTriangle />, 'warn',
              forcing ? 'Re-push this crew to Spectrum?' : 'Push this crew to Spectrum?',
              metaLine)}
            <div className="modal-body vx-confirm">
              <div className="vx-sumcard">
                <div className="vx-sumrow">
                  <span className="lb">Crew</span>
                  <span className="vl">{crewName} &middot; {preview.crewCode}</span>
                </div>
                <div className="vx-sumrow">
                  <span className="lb">Batch</span>
                  <span className="vl mono">{preview.batchCode}</span>
                </div>
                <div className="vx-sumrow">
                  <span className="lb">Employees</span>
                  <span className="vl mono">{selectedIDs.length}</span>
                </div>
                {selectedPoKeys.size > 0 && (
                  <div className="vx-sumrow">
                    <span className="lb">PO phases</span>
                    <span className="vl mono">{selectedPoKeys.size} (2 rows each)</span>
                  </div>
                )}
                <div className="vx-sumrow tot">
                  <span className="lb">Rows</span>
                  <span className="vl mono">{selectedRows.length}</span>
                </div>
              </div>

              <p className="tr-exp-intro">
                Push {selectedRows.length} row{selectedRows.length === 1 ? '' : 's'} for {selectedIDs.length} employee
                {selectedIDs.length === 1 ? '' : 's'} as batch {preview.batchCode}?
              </p>

              {forcing ? (
                <div className="vx-note warn">
                  <AlertTriangle />
                  <span>
                    <b>These employees were already pushed as batch {preview.batchCode}.</b>{' '}
                    Spectrum has no delete: remove this crew&rsquo;s un-approved rows in Spectrum
                    first, then re-push. A forced push APPENDS to the same batch — if the earlier
                    rows are still there the hours will be counted twice.
                  </span>
                </div>
              ) : (
                <div className="vx-note info">
                  <Info />
                  <span>
                    The rows land in Spectrum&rsquo;s <b>Payroll › Pre-Time Card Import Errors</b> screen
                    for the clerk to Recheck. Spectrum has no delete API, so a mistake has to be
                    cleared there by hand.
                  </span>
                </div>
              )}
            </div>
            <div className="modal-foot tr-exp-foot">
              <button className="tr-btn" onClick={() => setPhase('select')} type="button">Back</button>
              <span className="vx-grow" />
              <button className="tr-btn" onClick={onClose} type="button">Cancel</button>
              <button
                className={`tr-btn ${forcing ? 'danger' : 'primary'}`}
                onClick={push}
                type="button"
              >
                <UploadCloud /> {forcing ? 'Re-push anyway' : 'Push to Spectrum'}
              </button>
            </div>
          </>
        )}

        {/* ── pushing ───────────────────────────────────────────────────── */}
        {phase === 'pushing' && (
          <>
            {head(<UploadCloud />, '', 'Pushing to Spectrum…', metaLine)}
            <div className="modal-body">
              <div className="vx-starting">
                <div className="vx-spin" />
                <span className="t">Sending {selectedRows.length} row{selectedRows.length === 1 ? '' : 's'}…</span>
                <span className="s">Spectrum accepts or refuses the whole batch — don&rsquo;t close this window.</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SpectrumExportModal;
